import React, { ReactNode, useState } from 'react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import {
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CopyOutlined
} from '@ant-design/icons';

import { MCP_MONO } from '../../utils/mcp';
import type { DocsBlock } from './docsContent';

// Shared MCP UI primitives — kept here so the hub, the per-account editor and
// the activity feed share one calm, dashboard-consistent look (esp. in dark mode).

type Severity = 'info' | 'warning' | 'success' | 'error';

const ICONS: Record<Severity, ReactNode> = {
  info: <InfoCircleOutlined />,
  warning: <WarningOutlined />,
  success: <CheckCircleOutlined />,
  error: <CloseCircleOutlined />
};

// SoftAlert — an alpha-tinted callout. Replaces MUI's <Alert>, whose saturated
// fills read as harsh in dark mode. Subtle tint + hairline border + muted text.
// Keeps Alert's SEMANTICS: errors/warnings interrupt (role=alert), info/success
// announce politely (role=status) — otherwise screen readers never hear the
// compile errors this renders in the indicator editor.
export const SoftAlert = ({ severity = 'info', children, sx }: { severity?: Severity; children: ReactNode; sx?: object }) => (
  <Box
    role={severity === 'error' || severity === 'warning' ? 'alert' : 'status'}
    sx={(theme) => {
      const c = theme.palette[severity].main;
      const dark = theme.palette.mode === 'dark';
      return {
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        p: 1.5,
        pl: 1.75,
        borderRadius: '12px',
        bgcolor: alpha(c, dark ? 0.1 : 0.07),
        border: `1px solid ${alpha(c, dark ? 0.22 : 0.18)}`,
        ...sx
      };
    }}
  >
    <Box
      sx={(theme) => ({
        color: theme.palette[severity].main,
        display: 'flex',
        fontSize: 16,
        lineHeight: 1,
        mt: '2px',
        flexShrink: 0
      })}
    >
      {ICONS[severity]}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
      {children}
    </Typography>
  </Box>
);

// mcpFocusSx — a keyboard focus-visible ring for buttons/icon buttons, matching
// mcpInputSx's focused field ring so tabbing has one consistent signal.
export const mcpFocusSx = (theme: Theme) => ({
  '&.Mui-focusVisible': { outline: 'none', boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}` }
});

// ctaSx — the AI Connect purchase CTA: gradient fill, soft glow halo, a sheen
// that sweeps on hover, and a gentle lift. One button language across the
// intro page, the buy modal and the per-account section, so "the way in" is
// unmistakable everywhere it appears. Falls back gracefully under
// prefers-reduced-motion (no sweep, no lift — the glow alone carries it).
export const ctaSx = (theme: Theme) => {
  const p = theme.palette.primary;
  return {
    ...mcpFocusSx(theme),
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '12px',
    px: 3,
    fontWeight: 700,
    letterSpacing: '0.01em',
    color: p.contrastText,
    background: `linear-gradient(135deg, ${p.dark} 0%, ${p.main} 55%, ${p.light} 130%)`,
    boxShadow: `0 8px 24px -8px ${alpha(p.main, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.25)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(110deg, transparent 30%, ${alpha('#fff', 0.28)} 50%, transparent 70%)`,
      transform: 'translateX(-120%)',
      pointerEvents: 'none'
    },
    '&:hover': {
      background: `linear-gradient(135deg, ${p.dark} 0%, ${p.main} 45%, ${p.light} 115%)`,
      boxShadow: `0 12px 32px -8px ${alpha(p.main, 0.65)}, inset 0 1px 0 ${alpha('#fff', 0.3)}`,
      transform: 'translateY(-1px)'
    },
    '&:hover::after': { transition: 'transform .6s ease', transform: 'translateX(120%)' },
    '&:active': { transform: 'translateY(0)' },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::after': { display: 'none' },
      '&:hover': { transform: 'none' }
    }
  } as const;
};

// mcpInputSx — a refined TextField skin: subtle fill, 10px radius, teal focus
// ring. Makes inputs feel tactile instead of flat, and softens them in dark mode.
export const mcpInputSx = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark';
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: dark ? alpha('#fff', 0.04) : alpha(theme.palette.text.primary, 0.02),
      transition: 'background-color .15s ease, box-shadow .15s ease',
      '& fieldset': { borderColor: theme.palette.divider, transition: 'border-color .15s ease' },
      '&:hover': { backgroundColor: dark ? alpha('#fff', 0.06) : alpha(theme.palette.text.primary, 0.03) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.45) },
      '&.Mui-focused': { backgroundColor: dark ? alpha('#fff', 0.06) : '#fff' },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`
      }
    }
  };
};

// SignalIllustration — a mini AI → gate → account schematic for empty states:
// the one place a custom illustration earns its keep (new users see it first).
export const SignalIllustration = () => {
  const theme = useTheme();
  const line = theme.palette.divider;
  const teal = theme.palette.primary.main;
  return (
    <Box component="svg" viewBox="0 0 180 44" aria-hidden sx={{ width: 180, height: 44, display: 'block', mx: 'auto', mb: 1.5 }}>
      <rect x="1" y="10" width="44" height="24" rx="7" fill="none" stroke={line} />
      <rect x="68" y="6" width="44" height="32" rx="8" fill={alpha(teal, 0.06)} stroke={alpha(teal, 0.45)} />
      <rect x="135" y="10" width="44" height="24" rx="7" fill="none" stroke={line} />
      <line x1="45" y1="22" x2="68" y2="22" stroke={line} strokeWidth="1.5" />
      <line x1="112" y1="22" x2="135" y2="22" stroke={line} strokeWidth="1.5" />
      <circle cx="23" cy="22" r="3" fill={alpha(teal, 0.5)} />
      <circle cx="90" cy="22" r="3.5" fill={teal} />
      <circle cx="157" cy="22" r="3" fill={alpha(teal, 0.5)} />
    </Box>
  );
};

// EmptyState — a small icon chip (or custom illustration) + a title + an
// optional hint, centered. For lists that have nothing yet instead of a bare line.
export const EmptyState = ({
  icon,
  illustration,
  title,
  hint
}: {
  icon?: ReactNode;
  illustration?: ReactNode;
  title: string;
  hint?: string;
}) => (
  <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
    {illustration}
    {!illustration && icon && (
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          mx: 'auto',
          mb: 1.25,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 19,
          color: theme.palette.text.secondary,
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04)
        })}
      >
        {icon}
      </Box>
    )}
    <Typography variant="body1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

// Skeleton rows for the accounts table while settings load — steadier than a
// centered spinner (no layout jump, keeps the panel height).
export const AccountRowsSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <Box sx={{ pt: 1 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          borderTop: i === 0 ? 'none' : `1px solid ${theme.palette.divider}`
        })}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="45%" height={18} />
          <Skeleton variant="text" width="28%" height={13} />
        </Box>
        <Skeleton variant="rounded" width={52} height={16} />
        <Skeleton variant="text" width={88} height={16} sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Skeleton variant="rounded" width={60} height={26} />
      </Box>
    ))}
  </Box>
);

// Skeleton rows for the activity timeline.
export const ActivityRowsSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          py: 1.5,
          borderTop: i === 0 ? 'none' : `1px solid ${theme.palette.divider}`
        })}
      >
        <Skeleton variant="circular" width={8} height={8} sx={{ mt: 0.75, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="55%" height={18} />
          <Skeleton variant="text" width="35%" height={13} />
        </Box>
        <Skeleton variant="text" width={64} height={13} />
      </Box>
    ))}
  </Box>
);

// McpCodeBlock renders one copyable command or config block from the docs
// content. Shared by the docs page and the hub's connect card so a client's
// instructions are written once — the hub used to keep its own abbreviated copy
// of every client's steps, which drifted into telling OpenClaw users to paste
// "<the URL below>" into a shell.
//
// Owns its own copied state: blocks are independent, and a page-level flag lit
// every button at once. `dense` is the dashboard-card size.
export const McpCodeBlock = ({ block, dense = false }: { block: DocsBlock; dense?: boolean }) => {
  const theme = useTheme();
  const p = theme.palette.primary;
  const [copied, setCopied] = useState(false);

  const body = block.kind === 'config' ? block.code : block.lines.join('\n');
  // `copy` overrides the clipboard when the displayed block carries '#'
  // commentary, or commands meant for two different machines.
  const clipboard = block.kind === 'config' ? block.code : block.copy ?? body;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(clipboard);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (permissions/http) — the text is selectable */
    }
  };

  return (
    <Box sx={{ mt: dense ? 0.75 : 1.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography
          sx={{
            fontFamily: MCP_MONO,
            fontSize: dense ? 9 : 9.5,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary'
          }}
        >
          {block.kind === 'config' ? block.path : 'terminal'}
        </Typography>
        <Button
          size="small"
          variant="text"
          startIcon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={onCopy}
          sx={{ ...mcpFocusSx(theme), minWidth: 0, px: 0.9, py: 0.2, fontSize: dense ? 10.5 : 11.5 }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          px: dense ? 1.1 : 1.5,
          py: dense ? 0.85 : 1.15,
          borderRadius: '10px',
          border: `1px solid ${alpha(p.main, 0.22)}`,
          bgcolor: theme.palette.mode === 'dark' ? alpha('#000', 0.32) : alpha(p.main, 0.045),
          fontFamily: MCP_MONO,
          fontSize: dense ? 11 : 12.5,
          lineHeight: 1.6,
          color: 'text.primary',
          whiteSpace: 'pre',
          overflowX: 'auto'
        }}
      >
        {body}
      </Box>
    </Box>
  );
};
